import ObjectDataStore from "./ObjectDataStore";
import EventManager from "../components/EventManager";

export type ListenerFunction<E extends Element> = (elem:E) => Exclude<any, undefined>;

export type ProcessedEvent = {added: Map<Element, Array<any>>, removed: Map<Element, Array<any>>};

export enum IgnorableActionKey {
	add = "add",
	remove = "remove"
}

export interface EventMap {
	// Triggered on each mutation and runs before listeners are notified
	"mutation": {mutationRecords: MutationRecord[]};

	// The "added" and "removed" are a map of elements which were processed and an array of what each onAdd() or onRemove() returns.
	"processed": ProcessedEvent;
}

/**
 * Watches for when elements  with a specific style class, tag name, etc. are added or removed to `rootElement` or subtree of `rootElement`.
 * This class keeps track of notifications to simplify management of the process of watching for elements added to the document.
 * By default, `rootElement` is `window.document.documentElement`. 
 */
export default class ElementAndSubtreeObserver<MD> {
	private static readonly instances = new WeakMap<Element, ElementAndSubtreeObserver<unknown>>();

	private readonly mutationObserver: MutationObserver;
	private readonly observerEntries = new Array<ObserverEntry<any, MD>>();
	private ignoreDataStore: ObjectDataStore<{ [K in keyof typeof IgnorableActionKey]: boolean }> | undefined;

	private readonly eventManager = new EventManager<EventMap>();
	public readonly addEventListener = this.eventManager.addEventListener;
	public readonly removeEventListener = this.eventManager.removeEventListener;

	private sortingFunction?: ElementAndSubtreeObserverSortingFunction<MD>
	private needsSortingValue = false;

	public static getInstance<MD>(rootElement: Element = window.document.documentElement): ElementAndSubtreeObserver<MD> {
		let observer = ElementAndSubtreeObserver.instances.get(rootElement);
		if (observer === undefined) {
			observer = new ElementAndSubtreeObserver(rootElement);
			ElementAndSubtreeObserver.instances.set(rootElement, observer);
		}
		return observer as ElementAndSubtreeObserver<MD>;
	}

	private constructor(
		private readonly rootElement: Element,
	) {
		this.mutationObserver = new MutationObserver(this.onMutation);
		this.mutationObserver.observe(this.rootElement, {
			childList: true,
			subtree: true,
			characterData: false,
			attributes: false
		});

		/* TODO @Michael: Watch for mutations which causes rootElement to be become isConnected === false ?
				We would need to call disconnect() and call observe() again.
				And also update each registered component: Cleanup, and then "start over" for example new live node lists for each component type.
			Is it even possible for documentElement to be replaced though? Aaah but there is no reason that the root element we are watching is a "documentElement".
				I renamed. So now we do need to do this.
		*/
	}
	
	public setSortingFunction(sortingFunction: ElementAndSubtreeObserverSortingFunction<MD>) {
		this.sortingFunction = sortingFunction;
		this.needsSorting();
	}

	private performSorting(needsSorting: boolean = this.needsSortingValue) {
		if (needsSorting) {
			this.needsSortingValue = false;
			if (this.sortingFunction !== undefined) {
				this.observerEntries.sort(this.sortingFunction);
			}
		}
	}

	private onMutation = (mutationRecords: MutationRecord[]) => {
		// The "mutation" event is triggered for all mutations; no matter if any other work is done.
		this.eventManager.notifyEventListeners("mutation", {mutationRecords: mutationRecords});

		// If there are not "processed" listeners or observerEntries, there is nothing more to do.
		if (this.observerEntries.length === 0 && !this.eventManager.hasEventListeners("processed")) {
			return;
		}

		// If there is only one mutation record and there is only one add and remove, and both are
		// text nodes, it is simply a change in a text on the page and therefore nothing more to process.
		if (
			mutationRecords.length === 1
			&& mutationRecords[0]!.addedNodes.length === 1 && mutationRecords[0]!.removedNodes.length === 1
			&& mutationRecords[0]!.addedNodes[0]!.nodeType === Node.TEXT_NODE && mutationRecords[0]!.removedNodes[0]!.nodeType === Node.TEXT_NODE
		) {
			return;
		}

		// Ignore a simple move.
		// This will not skimp more complex DOM manipulation but will skip when we move just one element
		// NOTE: Due to how we process elements, even if we didn't skip this, a component instance (if there is one on
		// the element) would still not be destroyed and re-added if it was just a move. If we do a larger manipulation,
		// the records will be more complex and this won't skip it. Purpose of this is just to skip extra processing
		// when not needed.
		if (
			mutationRecords.length === 2
			&& mutationRecords[0]!.addedNodes.length === 0 && mutationRecords[0]!.removedNodes.length === 1	// One removed
			&& mutationRecords[1]!.addedNodes.length === 1 && mutationRecords[1]!.removedNodes.length === 0	// One added
			&& mutationRecords[0]!.removedNodes[0] === mutationRecords[1]!.addedNodes[0]					// Same node
		) {
			return;
		}


		// Did the mutation include an add and/or remove entries? Knowing this can greatly reduce work each mutation.
		// Also check for and remove any moving of DOM elements within the same document (there is a remove
		let mayHaveAdded = false;
		let mayHaveRemoved = false;
		for (let i = 0; i < mutationRecords.length; i++) {
			let record = mutationRecords[i]!;
			if (record.addedNodes.length !== 0) {
				mayHaveAdded = true;
				if (mayHaveRemoved) {
					break; // If we get here, then both mayHaveAdded and mayHaveRemoved have been set to true, so we can break
				}
			}
			if (record.removedNodes.length !== 0) {
				mayHaveRemoved = true;
				if (mayHaveAdded) {
					break; // If we get here, then both mayHaveAdded and mayHaveRemoved have been set to true, so we can break
				}
			}
		}
		if (!mayHaveAdded && !mayHaveRemoved) {
			return; // Nothing to do; so stop work. This should never happen.
		}

		// Update the sorting of the observerEntries before they are used
		this.performSorting();

		// Maps to store a list of added and removed (along with the result of onAdd and onRemove calls)
		const addedAndResult = new Map<Element, Array<any>>();
		const removedAndResult = new Map<Element, Array<any>>();

		// Notify each observer entry and collect results
		for (let i = 0; i < this.observerEntries.length; i++) {
			const processed = this.observerEntries[i]!.processElements(this, mayHaveAdded, mayHaveRemoved);
			if (processed !== null) {
				processed.added.forEach((value, element) => {
					const addedAndResultForElement = addedAndResult.get(element);
					if (addedAndResultForElement !== undefined) {
						addedAndResultForElement.push(...value);
					} else {
						addedAndResult.set(element, [...value]);
					}
				});
				processed.removed.forEach((value, element) => {
					const removedAndResultForElement = removedAndResult.get(element);
					if (removedAndResultForElement !== undefined) {
						removedAndResultForElement.push(...value);
					} else {
						removedAndResult.set(element, [...value]);
					}
				});
			}
		}

		// Trigger "processed" event
		if (addedAndResult.size > 0 || removedAndResult.size > 0) {
			this.eventManager.notifyEventListeners("processed", {added: addedAndResult, removed: removedAndResult});
		}
	}

	/**
	 * Listeners will not be notified of the passed element for add and/or remove events.
	 * This does NOT stop "mutation" events from being notified though.
	 * This method only marks the passed element. It does NOT mark all descendant Elements as well.
	 * If you want to mark all descendants too, see ignoreElementAndDescendants().
	 */
	public ignoreElement(element: Element, ignoreAdd: boolean, ignoreRemove: boolean) {
		if (this.ignoreDataStore === undefined) {
			this.ignoreDataStore = new ObjectDataStore();
		}
		this.storeIgnoredElement(element, ignoreAdd, ignoreRemove);
	}

	/**
	 * Same as ignoreElement() but affects all descendant Elements as well.
	 */
	public ignoreElementAndDescendants(element: Element, ignoreAdd: boolean, ignoreRemove: boolean) {
		if (this.ignoreDataStore === undefined) {
			this.ignoreDataStore = new ObjectDataStore();
		}
		this.storeIgnoredElement(element, ignoreAdd, ignoreRemove);
		const descendants = element.getElementsByTagName("*"); // The special string "*" represents all elements.
		for (let i = 0; i < descendants.length; i++) {
			this.storeIgnoredElement(descendants[i]!, ignoreAdd, ignoreRemove);
		}
	}

	// NOTE: Only use this private method when you can guarantee that this.ignoreDataStore is not undefined
	private storeIgnoredElement(element: Element, ignoreAdd: boolean, ignoreRemove: boolean) {
		this.ignoreDataStore!.set(element, IgnorableActionKey.add, ignoreAdd);
		this.ignoreDataStore!.set(element, IgnorableActionKey.remove, ignoreRemove);
	}

	isElementAddIgnored(element: Element): boolean {
		return this.isElementActionIgnored(element, IgnorableActionKey.add);
	}
	isElementRemoveIgnored(element: Element): boolean {
		return this.isElementActionIgnored(element, IgnorableActionKey.remove);
	}
	private isElementActionIgnored(element: Element, key: IgnorableActionKey) {
		return this.ignoreDataStore !== undefined && this.ignoreDataStore.get(element, key, false);
	}


	onLiveCollection<E extends Element>(
		liveCollection: HTMLCollectionOf<E>,
		onAdd: ListenerFunction<E>,
		onRemove?: ListenerFunction<E>,
		metadata?: MD
	): ObserverEntry<E, MD> {
		const entry = new ObserverEntry(liveCollection, onAdd, onRemove, metadata);

		this.observerEntries.push(entry);
		this.needsSorting();

		// Call once because there may be some existing elements already, and trigger "processed" event.
		const processed = entry.processElements(this);
		if (processed !== null && processed.added.size > 0) {
			// Don't check "removed" since this is the first time this was ran so it will always be empty.
			this.eventManager.notifyEventListeners("processed", {added: processed.added, removed: processed.removed});
		}

		return entry;
	}

	onElementWithStyleClass<E extends Element>(
		styleClass: string, onAdd: ListenerFunction<E>, onRemove?: ListenerFunction<E>, metadata?: MD
	): ObserverEntry<E, MD> {
		const liveCollection = this.rootElement.getElementsByClassName(styleClass) as HTMLCollectionOf<E>;
		return this.onLiveCollection(liveCollection, onAdd, onRemove, metadata);
	}

	onElementWithTagName<K extends keyof HTMLElementTagNameMap>(
		tagName: K, onAdd: ListenerFunction<HTMLElementTagNameMap[K]>, onRemove?: ListenerFunction<HTMLElementTagNameMap[K]>, metadata?: MD
	): ObserverEntry<HTMLElementTagNameMap[K], MD>;
	onElementWithTagName<E extends Element>(
		tagName: string, onAdd: ListenerFunction<E>, onRemove?: ListenerFunction<E>, metadata?: MD
	): ObserverEntry<E, MD>;
	onElementWithTagName<E extends Element>(
		tagName: string, onAdd: ListenerFunction<E>, onRemove?: ListenerFunction<E>, metadata?: MD
	): ObserverEntry<E, MD> {
		const liveCollection = this.rootElement.getElementsByTagName(tagName) as HTMLCollectionOf<E>;
		return this.onLiveCollection(liveCollection, onAdd, onRemove, metadata);
	}

	onElementWithTagNameNS<E extends Element>(
		namespaceUri: string, localName: string, onAdd: ListenerFunction<E>, onRemove?: ListenerFunction<E>, metadata?: MD
	): ObserverEntry<E, MD> {
		const liveCollection = this.rootElement.getElementsByTagNameNS(namespaceUri, localName) as HTMLCollectionOf<E>;
		return this.onLiveCollection(liveCollection, onAdd, onRemove, metadata);
	}


	destroy () {
		this.eventManager.destroy();
		ElementAndSubtreeObserver.instances.delete(this.rootElement);
		this.mutationObserver.disconnect();
	}

	public needsSorting() {
		this.needsSortingValue = true;
	}
}



// =================== Private Class =================
/**
 * To help keep track of and determine what elements have changed over time.
 */
export class ObserverEntry<E extends Element, MD> {
	private static readonly keyForSetOfNotifiedInstanceUIDs = "notifiedUIDs";
	private static readonly dataStore = new ObjectDataStore<{[ObserverEntry.keyForSetOfNotifiedInstanceUIDs]: Set<number>}>();
	private static uidCount = 0;

	private readonly uid: number;

	private processedElements: E[] | undefined;

	constructor(
		private readonly liveCollection: HTMLCollectionOf<E>,
	    private readonly onAdd: ListenerFunction<E>,
	    private readonly onRemove?: ListenerFunction<E>,
	    private readonly metadata?: MD
	) {
		this.uid = ObserverEntry.uidCount++;
	}

	public getLiveCollection(): HTMLCollectionOf<E> {
		return this.liveCollection;
	}

	public getMetadata(): MD | undefined {
		return this.metadata;
	}

	private static getListOfNotified(elem:Element): Set<number> {
		let listOfProcessed = ObserverEntry.dataStore.get(elem, ObserverEntry.keyForSetOfNotifiedInstanceUIDs);
		if (listOfProcessed === undefined) {
			listOfProcessed = ObserverEntry.dataStore.set(elem, ObserverEntry.keyForSetOfNotifiedInstanceUIDs, new Set<number>());
		}
		return listOfProcessed;
	}
	private isProcessed(elem:Element): boolean {
		const listOfProcessed = ObserverEntry.getListOfNotified(elem);
		return listOfProcessed.has(this.uid);
	}
	private setProcessed(elem:Element, isProcessed: boolean) {
		const listOfProcessed = ObserverEntry.getListOfNotified(elem);
		if (isProcessed) {
			listOfProcessed.add(this.uid);
		} else {
			listOfProcessed.delete(this.uid);
		}
	}

	public processElements(elementObserver: ElementAndSubtreeObserver<MD>, mayHaveAdded = true, mayHaveRemoved = true): ProcessedEvent | null {
		// If there was either an add or remove (but not both) in the mutation, but the number of elements last time
		// and this time are the same, then there can not be any changes and we can skip the work.
		if (
			mayHaveAdded !== mayHaveRemoved
			&& this.processedElements !== undefined
			&& this.processedElements.length === this.liveCollection.length
		) {
			return null;
		}

		// Processes the elements
		const removedElements = this.processedElements !== undefined ? this.processedElements : new Array<E>();
		this.processedElements = new Array<E>();
		const addedElements = new Array<E>();

		const removedAndResult = new Map<E, Array<any>>();
		const addedAndResult = new Map<E, Array<any>>();

		for (let i = 0; i < this.liveCollection.length; i++) {
			let elem = this.liveCollection[i]!;
			// Keep a list of what elements which were added (can't process yet because we need to trigger removes first)
			if (!elementObserver.isElementAddIgnored(elem) && !this.isProcessed(elem)) {
				addedElements.push(elem);
			}
			
			// "Remove" the element from the array to figure out what is removed (what ever is left in the array must have been removed)
			// We don't actually "remove" the item but instead set to "null" and ignore those when iterating over them later.
			const elemIndexInRemovedArray = removedElements.indexOf(elem);
			if (elemIndexInRemovedArray !== -1) {
				removedElements.splice(elemIndexInRemovedArray, 1);
			}

			// Keep a list of all elements processed so we can check against it next time this method runs
			this.processedElements.push(elem);
		}

		// Take care of the items which were removed (aka was processed previously but no longer in the list and therefore must have been removed)
		if (this.onRemove !== undefined && removedElements.length > 0) {
			removedElements.forEach(elem => {
				if (!elementObserver.isElementRemoveIgnored(elem)) {
					const result = this.onRemove!(elem);
					this.setProcessed(elem, false);
					const removedAndResultForElem = removedAndResult.get(elem);
					if (removedAndResultForElem !== undefined) {
						removedAndResultForElem.push(result);
					} else {
						removedAndResult.set(elem, [result]);
					}
				}
			});
		}

		// Notify listener of the new element (we know it is new because it has not been marked as processed previously).
		// This needs to be after onRemove listeners are called
		if (addedElements.length > 0) {
			addedElements.forEach(elem => {
				const result = this.onAdd(elem);
				this.setProcessed(elem, true);
				const addedAndResultForElem = addedAndResult.get(elem);
				if (addedAndResultForElem !== undefined) {
					addedAndResultForElem.push(result);
				} else {
					addedAndResult.set(elem, [result]);
				}
			});
		}

		return {added: addedAndResult, removed: removedAndResult};
	}
}

export type ElementAndSubtreeObserverSortingFunction<MD> = (a: ObserverEntry<any, MD>, b: ObserverEntry<any, MD>) => number;
